import { useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

import {
  llmPlatformAPI,
  type LlmChannel,
  type LlmFailureLog,
} from '../../../baselines/llm/llmPlatformAPI';
import toast from '../../Toast';
import { formatBeijingDateTime } from '../../../utils/time';

const PAGE_SIZE = 50;

export default function LlmFailureLogsTab() {
  const [logs, setLogs] = useState<LlmFailureLog[]>([]);
  const [channels, setChannels] = useState<LlmChannel[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterCode, setFilterCode] = useState('');
  const [filterChannelId, setFilterChannelId] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const totalPages = useMemo(() => Math.ceil(total / PAGE_SIZE) || 1, [total]);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const [r, ch] = await Promise.all([
        llmPlatformAPI.listFailureLogs({
          page: p,
          limit: PAGE_SIZE,
          useCaseCode: filterCode || undefined,
          channelId: filterChannelId || undefined,
          startDate: filterStart || undefined,
          endDate: filterEnd || undefined,
        }),
        llmPlatformAPI.listChannels(),
      ]);
      setLogs(r.data);
      setTotal(r.pagination.total);
      setChannels(ch.data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCode, filterChannelId, filterStart, filterEnd]);

  const goPage = (np: number) => {
    if (np < 1 || np > totalPages) return;
    setPage(np);
    void load(np);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-light-text">LLM 失败日志</h3>
        <button
          onClick={() => void load(page)}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-light-border hover:bg-light-hover"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> 刷新
        </button>
      </div>

      <div className="bg-white rounded-xl border border-light-border p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          className="input"
          placeholder="筛选 useCaseCode"
          value={filterCode}
          onChange={(e) => setFilterCode(e.target.value)}
        />
        <select
          className="input"
          value={filterChannelId}
          onChange={(e) => setFilterChannelId(e.target.value)}
        >
          <option value="">全部渠道</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input
          type="date"
          className="input"
          value={filterStart}
          onChange={(e) => setFilterStart(e.target.value)}
        />
        <input
          type="date"
          className="input"
          value={filterEnd}
          onChange={(e) => setFilterEnd(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-light-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> 加载中
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-light-border overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-light-hover text-light-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">时间</th>
                  <th className="px-4 py-3 text-left font-medium">useCaseCode</th>
                  <th className="px-4 py-3 text-left font-medium">errorCode</th>
                  <th className="px-4 py-3 text-left font-medium">status</th>
                  <th className="px-4 py-3 text-left font-medium">errorMessage</th>
                  <th className="px-4 py-3 text-left font-medium">latency</th>
                  <th className="px-4 py-3 text-right font-medium">展开</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-light-muted">无失败日志</td></tr>
                )}
                {logs.map((log) => (
                  <FailureLogRow
                    key={log.id}
                    log={log}
                    expanded={expandedId === log.id}
                    onToggle={() => setExpandedId(expandedId === log.id ? null : log.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-light-border lg:hidden">
            {logs.length === 0 ? (
              <div className="px-4 py-8 text-center text-light-muted">无失败日志</div>
            ) : (
              logs.map((log) => {
                const expanded = expandedId === log.id;
                return (
                  <div key={log.id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-light-text">{log.useCaseCode ?? '-'}</div>
                        <div className="mt-1 text-xs text-light-muted">{formatBeijingDateTime(log.createdAt)}</div>
                      </div>
                      <button onClick={() => setExpandedId(expanded ? null : log.id)} className="text-xs text-blue-600">
                        {expanded ? '收起' : '详情'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-xs">{log.errorCode}</span>
                      <span className="px-1.5 py-0.5 rounded bg-light-hover text-light-secondary text-xs">{log.status ?? '-'}</span>
                      <span className="px-1.5 py-0.5 rounded bg-light-hover text-light-secondary text-xs">
                        {log.latencyMs != null ? `${log.latencyMs}ms` : '-'}
                      </span>
                    </div>
                    <div className="rounded-xl bg-light-hover p-3 text-xs text-light-text break-all">
                      {log.errorMessage}
                    </div>
                    {expanded && (
                      <div className="space-y-2 text-xs">
                        {log.requestSummary && (
                          <div>
                            <div className="text-light-muted">requestSummary</div>
                            <pre className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3">{log.requestSummary}</pre>
                          </div>
                        )}
                        {log.rawResponse && (
                          <div>
                            <div className="text-light-muted">rawResponse</div>
                            <pre className="mt-1 max-h-60 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3">{log.rawResponse}</pre>
                          </div>
                        )}
                        <div className="text-light-muted break-all">
                          userId: {log.userId ?? '-'} · channelId: {log.channelId ?? '-'} · modelId: {log.modelId ?? '-'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-light-muted">
        <div>共 {total} 条</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => goPage(page - 1)}
            disabled={page <= 1}
            className="inline-flex items-center px-2 py-1 rounded hover:bg-light-hover disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span>第 {page} / {totalPages} 页</span>
          <button
            onClick={() => goPage(page + 1)}
            disabled={page >= totalPages}
            className="inline-flex items-center px-2 py-1 rounded hover:bg-light-hover disabled:opacity-30"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function FailureLogRow({
  log,
  expanded,
  onToggle,
}: {
  log: LlmFailureLog;
  expanded: boolean;
  onToggle: () => void;
}) {
  const time = formatBeijingDateTime(log.createdAt);
  return (
    <>
      <tr className="border-t border-light-border">
        <td className="px-4 py-3 text-xs">{time}</td>
        <td className="px-4 py-3 font-mono text-xs">{log.useCaseCode ?? '-'}</td>
        <td className="px-4 py-3 text-xs">
          <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600">{log.errorCode}</span>
        </td>
        <td className="px-4 py-3 text-xs">{log.status ?? '-'}</td>
        <td className="px-4 py-3 text-xs truncate max-w-[420px]">{log.errorMessage}</td>
        <td className="px-4 py-3 text-xs">{log.latencyMs != null ? `${log.latencyMs}ms` : '-'}</td>
        <td className="px-4 py-3 text-right">
          <button
            onClick={onToggle}
            className="text-xs text-blue-600 hover:underline"
          >
            {expanded ? '收起' : '详情'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-light-border bg-gray-50/60">
          <td colSpan={7} className="px-4 py-3 text-xs space-y-2">
            <div>
              <span className="text-light-muted">errorMessage：</span>
              <pre className="whitespace-pre-wrap mt-1">{log.errorMessage}</pre>
            </div>
            {log.requestSummary && (
              <div>
                <span className="text-light-muted">requestSummary：</span>
                <pre className="whitespace-pre-wrap mt-1 max-h-40 overflow-y-auto">{log.requestSummary}</pre>
              </div>
            )}
            {log.rawResponse && (
              <div>
                <span className="text-light-muted">rawResponse（已脱敏，截断 4KB）：</span>
                <pre className="whitespace-pre-wrap mt-1 max-h-60 overflow-y-auto">{log.rawResponse}</pre>
              </div>
            )}
            <div className="text-light-muted">
              userId: {log.userId ?? '-'} · channelId: {log.channelId ?? '-'} · modelId: {log.modelId ?? '-'}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
